/**
 * Shared Auth Service
 *
 * Lógica de autenticación compartida entre web y mobile.
 * Maneja carga de perfiles, errores de tokens, y almacenamiento de sesión.
 *
 * USO:
 * import { authService } from '@shared/services/auth/authService';
 *
 * await authService.loadUserProfile(user);
 * const isValidToken = await authService.validateSession();
 */

import { supabase } from '@shared/libs/supabaseClient';
import { getRepositories } from '@shared/services/storage';

/**
 * Perfil de usuario cargado desde Supabase
 */
export interface UserProfile {
  user_id: string;
  empresa_activa_id: string;
  role: 'usuario' | 'admin';
}

/**
 * Resultado de carga de perfil
 */
export interface ProfileLoadResult {
  profile: UserProfile | null;
  fromCache: boolean;
}

/**
 * Auth Service - Lógica compartida de autenticación
 */
export const authService = {
  /**
   * Carga el perfil del usuario desde Supabase
   * Usa caché local primero para evitar llamadas innecesarias
   *
   * @param userId - ID del usuario de Supabase
   * @param forceRefresh - Forzar recarga desde servidor (ignorar caché)
   * @returns Perfil del usuario o null
   */
  async loadUserProfile(userId: string, forceRefresh: boolean = false): Promise<UserProfile | null> {
    try {
      // Primero intentar cargar desde storage local
      if (!forceRefresh) {
        const cached = await this.readCachedProfile(userId);
        if (cached) return cached;
      }

      console.log('[authService] Fetching profile from Supabase for user:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, empresa_activa_id, role')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.log('[authService] Error fetching profile:', error.message);
        // Offline-first: si el fetch falla (sin red), caer a la caché local
        // aunque sea forceRefresh. Sin esto, un arranque en frío sin conexión
        // perdía empresa_activa_id y la app caía en /acceso-denegado.
        return await this.readCachedProfile(userId);
      }

      const profileData = data as UserProfile;
      console.log('[authService] Profile fetched from DB:', profileData);

      // Guardar en storage local para futuras cargas. Best-effort: si la
      // caché local revienta (p.ej. navegador virgen donde el put del perfil
      // falla), el perfil de la BD IGUAL sirve — antes el catch lo descartaba
      // y la app quedaba sin rol (permisos rotos, /acceso-denegado).
      try {
        const { profile } = getRepositories();
        await profile.setCurrentProfile(
          profileData.user_id,
          profileData.empresa_activa_id,
          profileData.role
        );
      } catch (cacheError) {
        console.warn('[authService] Caché local de perfil no disponible:', cacheError);
      }

      return profileData;
    } catch (error) {
      console.error('[authService] Exception loading profile:', error);
      // Offline-first: ídem rama de error — intentar caché local antes de rendirse.
      return await this.readCachedProfile(userId);
    }
  },

  /**
   * Lee el profile del caché local, válido solo si es del usuario pedido.
   * Devuelve null si no hay caché o es de otro usuario.
   */
  async readCachedProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { profile } = getRepositories();
      const localProfile = await profile.getCurrentProfile();

      if (localProfile && localProfile.user_id === userId && localProfile.role) {
        console.log('[authService] Profile loaded from cache:', localProfile);
        return localProfile;
      }
      return null;
    } catch (error) {
      console.error('[authService] Error reading cached profile:', error);
      return null;
    }
  },

  /**
   * Guarda el perfil del usuario en storage local
   *
   * @param profile - Datos del perfil a guardar
   */
  async saveProfile(profile: UserProfile): Promise<void> {
    try {
      const { profile: profileRepo } = getRepositories();
      await profileRepo.setCurrentProfile(
        profile.user_id,
        profile.empresa_activa_id,
        profile.role
      );
      console.log('[authService] Profile saved to local storage:', profile);
    } catch (error) {
      console.error('[authService] Error saving profile:', error);
    }
  },

  /**
   * Obtiene el perfil actual desde storage local
   *
   * @returns Perfil actual o null
   */
  async getCurrentProfile(): Promise<UserProfile | null> {
    try {
      const { profile } = getRepositories();
      return (await profile.getCurrentProfile()) || null;
    } catch (error) {
      console.error('[authService] Error getting current profile:', error);
      return null;
    }
  },

  /**
   * Limpia el perfil almacenado localmente
   */
  async clearProfile(): Promise<void> {
    try {
      const { profile } = getRepositories();
      // Nota: ProfileRepository podría no tener un clear method
      // Esto depende de la implementación
      console.log('[authService] Profile cleared (if method exists)');
    } catch (error) {
      console.error('[authService] Error clearing profile:', error);
    }
  },

  /**
   * Determina si un usuario es admin basado en su perfil
   *
   * @param userId - ID del usuario a verificar
   * @returns true si el usuario es admin
   */
  async isAdmin(userId: string): Promise<boolean> {
    const profile = await this.loadUserProfile(userId);
    return profile?.role === 'admin';
  },

  /**
   * Obtiene la empresa activa del usuario actual
   *
   * @returns ID de la empresa activa o null
   */
  async getActiveEmpresa(): Promise<string | null> {
    const profile = await this.getCurrentProfile();
    return profile?.empresa_activa_id || null;
  }
};
